import { AfterViewInit, Component, ElementRef, Injector, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, SoftphoneService } from 'lib-trend-core';
import { combineLatest, takeUntil } from 'rxjs';
import { SoftphoneCallDialogComponent } from '../softphone-call-dialog/softphone-call-dialog.component';

/**
 * Always-mounted (authenticated shell) host for the softphone:
 * - owns the single, stable remote-audio sink so the SIP call survives navigation between pages;
 * - opens/closes the centered call modal purely from call state;
 * - surfaces call errors centrally.
 *
 * It renders no chrome of its own — the visible UI is the modal (here) and the topbar pill (header).
 */
@Component({
    selector: 'softphone-host',
    templateUrl: './softphone-host.component.html',
    styleUrls: ['./softphone-host.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SoftphoneHostComponent extends AbstractComponent implements AfterViewInit, OnDestroy {

  @ViewChild('remoteAudio') remoteAudioRef!: ElementRef<HTMLAudioElement>;

  private dialogRef?: MatDialogRef<SoftphoneCallDialogComponent>;
  // Tracks the specific dialog refs we close programmatically (call ended / minimized), so the
  // afterClosed handler can tell a programmatic close from a user dismiss without a shared boolean.
  private readonly programmaticCloses = new Set<MatDialogRef<SoftphoneCallDialogComponent>>();

  constructor(injector: Injector, public softphone: SoftphoneService, private dialog: MatDialog) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.softphone.attachAudio(this.remoteAudioRef.nativeElement);

    // The modal is visible whenever a call is in progress and not minimized — reconcile on every change.
    combineLatest([this.softphone.inProgress$, this.softphone.minimized$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reconcile());

    // Surface call errors (including the async dial-timeout) once, centrally.
    this.softphone.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message: string | null) => {
        if (message) {
          this.alertService.error(message);
          this.softphone.error$.next(null);
        }
      });
  }

  /** Single source of truth for modal visibility — safe to call after any state change or dialog close. */
  private reconcile(): void {
    const shouldShow = this.softphone.inProgress$.value && !this.softphone.minimized$.value;
    if (shouldShow && !this.dialogRef) {
      this.openDialog();
    } else if (!shouldShow && this.dialogRef) {
      this.hardCloseDialog();
    }
  }

  private openDialog(): void {
    const ref = this.dialog.open(SoftphoneCallDialogComponent, {
      width: '360px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'softphone-call-dialog',
    });
    this.dialogRef = ref;
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const wasProgrammatic = this.programmaticCloses.delete(ref);
        if (this.dialogRef === ref) {
          this.dialogRef = undefined;
        }
        // User dismissed the modal (X / Esc / backdrop / Minimizar) while the call is still up → minimize.
        if (!wasProgrammatic && this.softphone.inProgress$.value) {
          this.softphone.minimize();
        }
        // Re-evaluate in case state changed during the close (e.g. the pill was clicked mid-animation).
        this.reconcile();
      });
  }

  private hardCloseDialog(): void {
    if (this.dialogRef) {
      this.programmaticCloses.add(this.dialogRef);
      this.dialogRef.close();
    }
  }

  override ngOnDestroy(): void {
    // Authenticated shell tear-down (e.g. logout) — drop any live SIP session cleanly.
    this.softphone.disconnect();
    super.ngOnDestroy();
  }
}
